import { useEffect, useRef, useState } from 'react';
import { modifiers, Size } from '@outdoria/ui';
import { Form } from '../../../components';
import { CDN_URL } from '../../../env';
import { useImgSize } from '../../../components/Form/Image/Image';
import type { IFormValues, IRegister } from './types';

import s from '../ItemUpsertView.module.scss';

export type ImageType = Pick<
  IFormValues,
  'image' | 'image_eq' | 'thumbnail' | 'shape' | 'is_rotable'
>;

type ImageCreatorProps = {
  form: ImageType;
  register: IRegister<ImageType>;
};
export const ImageCreator = ({ form, register }: ImageCreatorProps) => {
  return (
    <div className={s.view__row}>
      <Form title="Image" icon="Image" pretty>
        <div className={s.view__form}>
          <Form.Input
            label={`${CDN_URL}...`}
            {...register('image', { required: true })}
          />

          <Form.Image src={`${CDN_URL}${form.image}`} />
        </div>
      </Form>
      <Form title="EQ image" icon="Image" pretty>
        <div className={s.view__form}>
          <Form.Input
            label={`${CDN_URL}...`}
            {...register('image_eq', { required: true })}
          />

          <ImageShapeCreator
            src={`${CDN_URL}${form.image_eq}`}
            {...register('shape', { required: true })}
          />

          <Form.Input
            label="Shape"
            disabled
            {...register('shape', { required: true })}
          />

          <Form.Switch
            label="Is rotable?"
            {...register('is_rotable', { required: true })}
          />
        </div>
      </Form>
      <Form title="Thumbnail" icon="Image" pretty>
        <div className={s.view__form}>
          <Form.Input
            label={`${CDN_URL}...`}
            {...register('thumbnail', { required: true })}
          />

          <Form.Image src={`${CDN_URL}${form.thumbnail}`} size={Size.BIG} />
        </div>
      </Form>
    </div>
  );
};

type ImageShapeCreatorProps = {
  src: string;
  value: string | null | undefined;
  setValue: (newValue: string) => void;
};
const ImageShapeCreator = ({
  src,
  value = '',
  setValue,
}: ImageShapeCreatorProps) => {
  const { ref, imgSize } = useImgSize();
  const [matrix, setMatrix] = useState<number[][]>([[0]]);
  const prevSrc = useRef<string>(src);

  useEffect(() => {
    // If there is an initial shape already set up, use it
    if (value) setMatrix(decodeMatrix());
    // If not, create it based on image's size
    else recalculateMatrix();
  }, [imgSize]);

  useEffect(() => {
    if (prevSrc.current !== src) {
      // Recalculate matrix if image had changed
      prevSrc.current = src;
      // Bugged af, doesn't work
      recalculateMatrix();
    }
  }, [src]);

  // String to matrix
  const decodeMatrix = () => {
    return (value ?? '').split('|').map(row => row.split(',').map(Number));
  };

  // Matrix to string
  const encodeMatrix = () => {
    return matrix.map(row => row.join(',')).join('|');
  };

  // Use new image to recalculate matrix size
  const recalculateMatrix = () => {
    const { width, height } = imgSize;

    if (width % 64 === 0 && height % 64 === 0) {
      const m = new Array(height / 64).fill(new Array(width / 64).fill(1));
      setMatrix(m);
    }
  };

  const isIncorrectSize = ref.current
    ? imgSize.width % 64 !== 0 || imgSize.height % 64 !== 0
    : false;

  const onCellClick = (x: number, y: number) => {
    setMatrix(prev => {
      const newMatrix = prev.map(row => [...row]);
      const prevCell = prev[y][x];
      const newCell = Math.abs(prevCell - 1);
      newMatrix[y][x] = newCell;
      return newMatrix;
    });
  };

  useEffect(() => {
    setValue(encodeMatrix());
  }, [matrix]);

  return (
    <>
      {isIncorrectSize && (
        <div className={s.creator__error}>
          <div>⚠️</div>
          <span>
            Width and height must be a multiplication of 64 (current:{' '}
            {imgSize.width} x {imgSize.height})
          </span>
        </div>
      )}

      <div className={s.creator}>
        <Form.Image ref={ref} src={src} size={Size.LARGE} />
        <div className={s.creator__keypad}>
          {matrix.map((row, y) => (
            <div className={s.row} key={y}>
              {row.map((cell, x) => (
                <button
                  key={x}
                  className={modifiers(s, 'cell', {
                    isSelected: cell === 1,
                  })}
                  onClick={() => onCellClick(x, y)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
